import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogTitle } from '@/components/design-system/dialog';
import { useDraggable } from '@/hooks/useDraggable';
import { cn } from '@/utils/cn';

export interface DialogRenderProps {
  close: () => void;
}

export interface DialogFooterRenderProps extends DialogRenderProps {
  isLoading?: boolean;
}

export interface DialogBuilderProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  defaultOpen?: boolean;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  showCloseButton?: boolean;
  header: (props: DialogRenderProps) => ReactNode;
  headerActions?: ReactNode;
  headerBorder?: boolean;
  content: (props: DialogRenderProps) => ReactNode;
  footer?: (props: DialogFooterRenderProps) => ReactNode;
  className?: string;
  isLoading?: boolean;
  draggable?: boolean;
}

// Fixed width classes to prevent dialog jumping when content changes
// sm: 384px, md: 448px, lg: 672-768px, xl: 768-1024px, full: 95% viewport
const sizeClasses = {
  sm: 'w-96 max-w-sm', // Fixed width: 384px (96 * 4px)
  md: 'w-[448px] max-w-md', // Fixed width: 448px
  lg: 'max-w-3xl min-w-2xl', // Range: 672px - 768px
  xl: 'max-w-5xl min-w-3xl', // Range: 768px - 1024px
  '2xl': 'max-w-7xl min-w-5xl',
  full: 'max-w-[95vw] min-w-[95vw]' // Full viewport width
};

export function DialogBuilder({
  open: controlledOpen,
  onOpenChange,
  defaultOpen,
  size = 'md',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  showCloseButton = true,
  header,
  headerActions,
  headerBorder = true,
  content,
  footer,
  className,
  isLoading = false,
  draggable
}: DialogBuilderProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : uncontrolledOpen;

  const isDraggable = draggable ?? true;

  const contentRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const { handlePointerDown } = useDraggable(contentRef, {
    enabled: isDraggable,
    headerRef,
    isOpen: open,
    wrapperRef
  });

  const handleOpenChange = useCallback(
    (newOpen: boolean) => {
      if (isControlled) {
        onOpenChange?.(newOpen);
      } else {
        setUncontrolledOpen(newOpen);
      }
    },
    [isControlled, onOpenChange]
  );
  const close = useCallback(() => {
    handleOpenChange(false);
  }, [handleOpenChange]);

  const footerProps: DialogFooterRenderProps = useMemo(
    () => ({
      close,
      isLoading
    }),
    [close, isLoading]
  );
  const renderProps: DialogRenderProps = useMemo(
    () => ({
      close
    }),
    [close]
  );

  if (!open) {
    return null;
  }

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        ref={contentRef}
        className={cn('flex max-h-[90vh] flex-col overflow-hidden p-0', sizeClasses[size], className)}
        wrapperRef={wrapperRef}
        showCloseButton={showCloseButton}
        disableDefaultTransform={isDraggable}
        onEscapeKeyDown={
          closeOnEscape
            ? undefined
            : (e) => {
                e.preventDefault();
              }
        }
        onPointerDownOutside={
          closeOnOverlayClick
            ? undefined
            : (e) => {
                e.preventDefault();
              }
        }
      >
        <div
          ref={headerRef}
          className={cn('shrink-0 px-6 py-4', headerBorder && 'border-b', isDraggable && 'cursor-move select-none')}
          onPointerDown={handlePointerDown}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">{header(renderProps)}</div>
            {headerActions !== undefined && headerActions !== null && <div className="shrink-0">{headerActions}</div>}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6">{content(renderProps)}</div>
        {footer && <div className="shrink-0 border-t px-6 py-4">{footer(footerProps)}</div>}
      </DialogContent>
    </Dialog>
  );
}

export { DialogTitle, DialogDescription, DialogClose };
